/**
 * Authentication context — Bearer token based.
 *
 * After WorkOS login, the callback redirects here with a JWT in the URL
 * fragment. The token is stored in localStorage and sent as
 * Authorization: Bearer on every API call.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const TOKEN_KEY = 'cxs_token';
const USER_KEY = 'cxs_user';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  image_url: string | null;
}

export interface AuthOrganization {
  id: string | null;
  name: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  organization: AuthOrganization | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Decode JWT payload without verification (just for reading claims). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]!));
  } catch {
    return null;
  }
}

/** Check if a JWT is expired. */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 < Date.now();
}

/** Parse auth data from URL fragment (#token=xxx&email=yyy&...). */
function parseFragment(): { token: string; email: string; first_name: string; last_name: string; org_name: string } | null {
  const hash = window.location.hash;
  if (!hash || !hash.includes('token=')) return null;

  const params = new URLSearchParams(hash.substring(1));
  const token = params.get('token');
  if (!token) return null;

  return {
    token,
    email: params.get('email') || '',
    first_name: params.get('first_name') || '',
    last_name: params.get('last_name') || '',
    org_name: params.get('org_name') || '',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<AuthOrganization | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);

  // On mount: check URL fragment (post-login redirect) or localStorage (refresh)
  useEffect(() => {
    // 1. Check URL fragment for fresh token from OAuth callback
    const fragment = parseFragment();
    if (fragment && !isTokenExpired(fragment.token)) {
      const payload = decodeJwtPayload(fragment.token);
      const authUser: AuthUser = {
        id: (payload?.userId as string) || '',
        email: fragment.email,
        first_name: fragment.first_name || null,
        last_name: fragment.last_name || null,
        image_url: null,
      };
      const authOrg: AuthOrganization = {
        id: (payload?.accountId as string) || null,
        name: fragment.org_name || null,
      };

      setToken(fragment.token);
      setUser(authUser);
      setOrganization(authOrg);
      localStorage.setItem(TOKEN_KEY, fragment.token);
      localStorage.setItem(USER_KEY, JSON.stringify({ user: authUser, organization: authOrg }));

      // Clear fragment from URL
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setLoading(false);
      return;
    }

    // 2. Check localStorage for existing token
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken && !isTokenExpired(storedToken)) {
      const stored = localStorage.getItem(USER_KEY);
      if (stored) {
        try {
          const { user: u, organization: o } = JSON.parse(stored);
          setToken(storedToken);
          setUser(u);
          setOrganization(o);
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }
    } else if (storedToken) {
      // Token expired — clear
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }

    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setOrganization(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        token,
        isAuthenticated: !!token,
        loading,
        error,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}

/**
 * Get the stored auth token. Can be called outside React components.
 * Returns null if not authenticated or token expired.
 */
export function getAuthToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token || isTokenExpired(token)) return null;
  return token;
}
